import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  approveTenant,
  fetchAdminStats,
  fetchPendingTenants,
  fetchRecentActivity,
  rejectTenant,
} from '../../api/admin';
import StatCard from '../../components/admin/StatCard';
import RejectModal from '../../components/admin/RejectModal';
import DashboardShell from '../../components/ui/DashboardShell';
import { useToast } from '../../context/ToastContext';
import { describeActivity } from '../../utils/activityLabels';

const AdminDashboardPage = () => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [rejectTenantId, setRejectTenantId] = useState(null);

  const { data: stats } = useQuery({ queryKey: ['admin-stats'], queryFn: fetchAdminStats });
  const { data: pending } = useQuery({ queryKey: ['admin-pending'], queryFn: fetchPendingTenants });
  const { data: activity } = useQuery({ queryKey: ['admin-activity'], queryFn: fetchRecentActivity });

  const approveMut = useMutation({
    mutationFn: approveTenant,
    onSuccess: () => {
      showToast(t('toast.tenantApproved'), 'success');
      queryClient.invalidateQueries({ queryKey: ['admin-stats'] });
      queryClient.invalidateQueries({ queryKey: ['admin-pending'] });
      queryClient.invalidateQueries({ queryKey: ['admin-activity'] });
    },
  });

  const rejectMut = useMutation({
    mutationFn: ({ id, reason }) => rejectTenant(id, reason),
    onSuccess: () => {
      showToast(t('toast.tenantRejected'), 'success');
      setRejectTenantId(null);
      queryClient.invalidateQueries({ queryKey: ['admin-pending'] });
      queryClient.invalidateQueries({ queryKey: ['admin-activity'] });
    },
  });

  return (
    <DashboardShell title={t('admin.dashboard.title')} subtitle={t('admin.dashboard.superAdminSubtitle')}>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label={t('admin.dashboard.totalSalons')}
          value={stats?.totalTenants ?? '—'}
          icon="🏢"
          trend={t('admin.dashboard.statTrendAllTenants')}
        />
        <StatCard
          label={t('common.active')}
          value={stats?.activeTenants ?? '—'}
          icon="✅"
          trend={t('admin.dashboard.statTrendLiveSubscriptions')}
          trendUp
        />
        <StatCard
          label={t('common.pending')}
          value={stats?.pendingRequests ?? '—'}
          icon="⏳"
          trend={t('admin.dashboard.statTrendAwaitingReview')}
        />
        <StatCard
          label={t('admin.dashboard.suspended')}
          value={stats?.suspendedTenants ?? '—'}
          icon="⚠️"
          trend={t('admin.dashboard.statTrendNeedsAttention')}
        />
      </div>

      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        <div className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-5">
          <p className="text-sm text-zinc-600 dark:text-zinc-500">{t('admin.dashboard.totalClients')}</p>
          <p className="mt-1 text-2xl font-semibold">{stats?.totalClients ?? '—'}</p>
        </div>
        <div className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-5">
          <p className="text-sm text-zinc-600 dark:text-zinc-500">{t('admin.dashboard.platformRevenue')}</p>
          <p className="mt-1 text-2xl font-semibold">
            ${(stats?.totalRevenue ?? 0).toLocaleString()}
          </p>
        </div>
      </div>

      <section className="mt-10">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-medium">{t('admin.dashboard.pendingRequests')}</h2>
          <Link to="/admin/tenants?status=PENDING" className="text-sm text-glow-400 hover:underline">
            {t('common.viewAll')}
          </Link>
        </div>
        <div className="overflow-x-auto rounded-xl border border-zinc-200 dark:border-zinc-800">
          <table className="w-full min-w-[640px] text-left text-sm">
            <thead className="border-b border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900/80 text-zinc-600 dark:text-zinc-500">
              <tr>
                <th className="px-4 py-3">{t('admin.tenants.salon')}</th>
                <th className="px-4 py-3">{t('admin.tenants.owner')}</th>
                <th className="px-4 py-3">{t('common.email')}</th>
                <th className="px-4 py-3">{t('admin.tenants.requestedAt')}</th>
                <th className="px-4 py-3">{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {(pending?.data || []).map((tenant) => (
                <tr key={tenant._id} className="border-b border-zinc-200/50 dark:border-zinc-800/50">
                  <td className="px-4 py-3 font-medium">{tenant.businessName}</td>
                  <td className="px-4 py-3">
                    {tenant.ownerUser
                      ? `${tenant.ownerUser.firstName} ${tenant.ownerUser.lastName}`
                      : tenant.ownerName}
                  </td>
                  <td className="px-4 py-3 text-zinc-600 dark:text-zinc-400">{tenant.email}</td>
                  <td className="px-4 py-3 text-zinc-600 dark:text-zinc-500">
                    {new Date(tenant.requestedAt).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => approveMut.mutate(tenant._id)}
                        className="rounded bg-emerald-600/20 px-2 py-1 text-xs text-emerald-300 hover:bg-emerald-600/30"
                      >
                        {t('admin.tenants.approve')}
                      </button>
                      <button
                        type="button"
                        onClick={() => setRejectTenantId(tenant._id)}
                        className="rounded bg-red-600/20 px-2 py-1 text-xs text-red-300 hover:bg-red-600/30"
                      >
                        {t('admin.tenants.reject')}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {!pending?.data?.length && (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-zinc-600 dark:text-zinc-500">
                    {t('admin.dashboard.noPendingRequests')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="mt-10">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-medium">{t('admin.dashboard.recentActivity')}</h2>
          <Link to="/admin/activity-logs" className="text-sm text-glow-400 hover:underline">
            {t('admin.dashboard.viewAllLogs')}
          </Link>
        </div>
        <div className="space-y-2">
          {(activity?.data || []).map((log) => (
            <div
              key={log.id}
              className="flex items-center justify-between rounded-lg border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 px-4 py-3 text-sm"
            >
              <div>
                <p>{describeActivity(log)}</p>
                <p className="text-xs text-zinc-600 dark:text-zinc-500">
                  {log.actor
                    ? `${log.actor.firstName} ${log.actor.lastName}`
                    : t('admin.dashboard.system')}{' '}
                  · {log.actor?.role}
                </p>
              </div>
              <span className="text-xs text-zinc-600 dark:text-zinc-500">
                {new Date(log.createdAt).toLocaleString()}
              </span>
            </div>
          ))}
        </div>
      </section>

      <RejectModal
        open={!!rejectTenantId}
        onClose={() => setRejectTenantId(null)}
        onConfirm={(reason) => rejectMut.mutate({ id: rejectTenantId, reason })}
        loading={rejectMut.isPending}
      />
    </DashboardShell>
  );
};

export default AdminDashboardPage;
